// Post-processes a dicebear-rendered bottts-neutral SVG to inject SMIL
// Animations: per-eye blinks (with occasional one-eye-only winks for two-eye
// Types) and idle mouth motion (EKG scroll, equalizer bounce, smile breath,
// Visor shimmer).
//
// Design notes
// ────────────
// - Animations are declarative SVG SMIL. They run without JS.
// - Per-actor ID suffix (a hash of the seed) is required because multiple
//   Avatars are inlined into the same DOM and SMIL `id` references in
//   Begin chains (`foo.end+4s`) are document-scoped, not SVG-scoped.
// - Wink tracks live alongside the base both-eyes blink as a second
//   `animateTransform` on the same wrapping `<g>`. additive="sum" composes
//   Harmlessly if a wink ever overlaps a base blink.
// - Wink intervals are coprime-ish (47s and 61s) and offset by per-actor
//   Amounts so different actors don't wink in unison.

// ────────────────────────────────────────────────────────────────────────────
// Hashing — short deterministic suffix to namespace IDs per actor
// ────────────────────────────────────────────────────────────────────────────

function hashSeed(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36).slice(0, 6) || "x";
}

// ────────────────────────────────────────────────────────────────────────────
// Eye recipes
// ────────────────────────────────────────────────────────────────────────────

const BLINK_DUR = "0.18s";
const BASE_BLINK_INTERVAL = 4; // Seconds between both-eyes blinks
const WINK_LEFT_INTERVAL = 47; // Seconds between left-eye-only winks
const WINK_RIGHT_INTERVAL = 61; // Seconds between right-eye-only winks

interface EyePoint {
  cx: number;
  cy: number;
  content: string;
}

// Wraps an eye's pupil(s) in translate→inverse-translate so an additive
// Scale animation orbits the eye's anatomical center. Adds the base blink
// Track plus, if `wink` is true, a second wink track on a longer interval.
function buildEyeGroup(
  point: EyePoint,
  baseId: string,
  baseBegin: number,
  wink: { id: string; begin: number; interval: number } | undefined,
): string {
  const wrapOpen = `<g transform="translate(${point.cx} ${point.cy})"><g transform="translate(${-point.cx} ${-point.cy})">${point.content}</g>`;
  const baseAnim = `<animateTransform attributeName="transform" type="scale" additive="sum" values="1 1; 1 0.05; 1 1" keyTimes="0; 0.5; 1" dur="${BLINK_DUR}" begin="${baseBegin}s; ${baseId}.end+${BASE_BLINK_INTERVAL}s" id="${baseId}"/>`;
  const winkAnim = wink
    ? `<animateTransform attributeName="transform" type="scale" additive="sum" values="1 1; 1 0.05; 1 1" keyTimes="0; 0.5; 1" dur="${BLINK_DUR}" begin="${wink.begin}s; ${wink.id}.end+${wink.interval}s" id="${wink.id}"/>`
    : "";
  return `${wrapOpen}${baseAnim}${winkAnim}</g>`;
}

interface EyeRecipe {
  kind: "blink" | "shimmer";
  original: string;
  animate: (hash: string) => string;
  needsShimmerDefs?: boolean;
}

// SMIL `<linearGradient>` whose stop offsets sweep across the visor every
// 2.4s. ID is hash-suffixed so multiple shimmer-eyed avatars on the page
// Don't share a definition.
function shimmerDefs(hash: string): string {
  const id = `visorShimmer_${hash}`;
  return `<defs><linearGradient id="${id}" x1="0" x2="0.3" y1="0" y2="0"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset="0.5" stop-color="#fff" stop-opacity="0.65"/><stop offset="1" stop-color="#fff" stop-opacity="0"/><animate attributeName="x1" values="-0.4; 1.0" dur="2.4s" begin="0s" repeatCount="indefinite"/><animate attributeName="x2" values="-0.1; 1.3" dur="2.4s" begin="0s" repeatCount="indefinite"/></linearGradient></defs>`;
}

const EYE_RECIPES: Record<string, EyeRecipe> = {
  eva: {
    animate: (hash) => {
      // Both eyes are in one path → can't wink independently. Blink jointly.
      const both = buildEyeGroup(
        {
          content: `<path d="M28.82 34.65c-6.53-1.35-11.24-6.34-10.52-11.14.72-4.79 6.6-7.58 13.12-6.23 6.53 1.36 11.24 6.35 10.52 11.15-.72 4.8-6.6 7.59-13.12 6.23ZM75.42 34.65c-6.52 1.36-12.4-1.43-13.12-6.23-.72-4.8 4-9.8 10.52-11.15 6.52-1.35 12.4 1.44 13.12 6.24.72 4.81-4 9.8-10.52 11.15Z" fill="#25A6F5"/>`,
          cx: 52,
          cy: 26,
        },
        `bEv_${hash}`,
        1.8,
        undefined,
      );
      return `<g fill-rule="evenodd" clip-rule="evenodd"><path d="M53 0c34.75 0 49 17.47 49 31 0 13.53-19.59 17-49 17-29.05 0-51-3.47-51-17S17.11 0 53 0Z" fill="#000" fill-opacity=".8"/>${both}</g>`;
    },
    kind: "blink",
    original: `<g fill-rule="evenodd" clip-rule="evenodd"><path d="M53 0c34.75 0 49 17.47 49 31 0 13.53-19.59 17-49 17-29.05 0-51-3.47-51-17S17.11 0 53 0Z" fill="#000" fill-opacity=".8"/><path d="M28.82 34.65c-6.53-1.35-11.24-6.34-10.52-11.14.72-4.79 6.6-7.58 13.12-6.23 6.53 1.36 11.24 6.35 10.52 11.15-.72 4.8-6.6 7.59-13.12 6.23ZM75.42 34.65c-6.52 1.36-12.4-1.43-13.12-6.23-.72-4.8 4-9.8 10.52-11.15 6.52-1.35 12.4 1.44 13.12 6.24.72 4.81-4 9.8-10.52 11.15Z" fill="#25A6F5"/></g>`,
  },
  frame1: {
    animate: (hash) => {
      const left = buildEyeGroup(
        {
          content: `<rect x="28" y="25" width="10" height="11" rx="2" fill="#8BDDFF"/>`,
          cx: 33,
          cy: 30.5,
        },
        `bF1L_${hash}`,
        1.5,
        {
          begin: 19 + (parseInt(hash, 36) % 11),
          id: `wF1L_${hash}`,
          interval: WINK_LEFT_INTERVAL,
        },
      );
      const right = buildEyeGroup(
        {
          content: `<rect x="66" y="25" width="10" height="11" rx="2" fill="#8BDDFF"/>`,
          cx: 71,
          cy: 30.5,
        },
        `bF1R_${hash}`,
        1.5,
        {
          begin: 35 + (parseInt(hash, 36) % 13),
          id: `wF1R_${hash}`,
          interval: WINK_RIGHT_INTERVAL,
        },
      );
      return `<rect y="4" width="104" height="42" rx="4" fill="#000" fill-opacity=".8"/>${left}${right}<path fill-rule="evenodd" clip-rule="evenodd" d="M21 4h8L12 46H4L21 4Z" fill="#fff" fill-opacity=".4"/>`;
    },
    kind: "blink",
    original: `<rect y="4" width="104" height="42" rx="4" fill="#000" fill-opacity=".8"/><rect x="28" y="25" width="10" height="11" rx="2" fill="#8BDDFF"/><rect x="66" y="25" width="10" height="11" rx="2" fill="#8BDDFF"/><path fill-rule="evenodd" clip-rule="evenodd" d="M21 4h8L12 46H4L21 4Z" fill="#fff" fill-opacity=".4"/>`,
  },
  frame2: {
    animate: (hash) => {
      const left = buildEyeGroup(
        {
          content: `<rect x="28" y="21" width="10" height="17" rx="2" fill="#5EF2B8"/>`,
          cx: 33,
          cy: 29.5,
        },
        `bF2L_${hash}`,
        1.7,
        {
          begin: 21 + (parseInt(hash, 36) % 12),
          id: `wF2L_${hash}`,
          interval: WINK_LEFT_INTERVAL,
        },
      );
      const right = buildEyeGroup(
        {
          content: `<rect x="66" y="21" width="10" height="17" rx="2" fill="#5EF2B8"/>`,
          cx: 71,
          cy: 29.5,
        },
        `bF2R_${hash}`,
        1.7,
        {
          begin: 38 + (parseInt(hash, 36) % 15),
          id: `wF2R_${hash}`,
          interval: WINK_RIGHT_INTERVAL,
        },
      );
      return `<rect x="8" y="10" width="88" height="36" rx="4" fill="#000" fill-opacity=".8"/>${left}${right}<path fill-rule="evenodd" clip-rule="evenodd" d="M83 10h5L76 46h-5l12-36Z" fill="#fff" fill-opacity=".4"/>`;
    },
    kind: "blink",
    original: `<rect x="8" y="10" width="88" height="36" rx="4" fill="#000" fill-opacity=".8"/><rect x="28" y="21" width="10" height="17" rx="2" fill="#5EF2B8"/><rect x="66" y="21" width="10" height="17" rx="2" fill="#5EF2B8"/><path fill-rule="evenodd" clip-rule="evenodd" d="M83 10h5L76 46h-5l12-36Z" fill="#fff" fill-opacity=".4"/>`,
  },
  glow: {
    animate: (hash) => {
      const leftContent = `<circle cx="21" cy="30" r="15" fill-opacity=".1"/><circle cx="21" cy="30" r="12" fill-opacity=".1"/><circle cx="21" cy="30" r="6" fill-opacity=".8"/><circle cx="21" cy="30" r="3" fill-opacity=".8"/>`;
      const rightContent = `<circle cx="83" cy="30" r="15" fill-opacity=".1"/><circle cx="83" cy="30" r="12" fill-opacity=".1"/><circle cx="83" cy="30" r="6" fill-opacity=".8"/><circle cx="83" cy="30" r="3" fill-opacity=".8"/>`;
      const left = buildEyeGroup(
        { content: leftContent, cx: 21, cy: 30 },
        `bGlL_${hash}`,
        2,
        {
          begin: 14 + (parseInt(hash, 36) % 13),
          id: `wGlL_${hash}`,
          interval: WINK_LEFT_INTERVAL,
        },
      );
      const right = buildEyeGroup(
        { content: rightContent, cx: 83, cy: 30 },
        `bGlR_${hash}`,
        2,
        {
          begin: 28 + (parseInt(hash, 36) % 17),
          id: `wGlR_${hash}`,
          interval: WINK_RIGHT_INTERVAL,
        },
      );
      return `<g fill="#fff">${left}${right}</g>`;
    },
    kind: "blink",
    original: `<g fill="#fff"><circle cx="21" cy="30" r="15" fill-opacity=".1"/><circle cx="83" cy="30" r="15" fill-opacity=".1"/><circle cx="21" cy="30" r="12" fill-opacity=".1"/><circle cx="83" cy="30" r="12" fill-opacity=".1"/><circle cx="21" cy="30" r="6" fill-opacity=".8"/><circle cx="83" cy="30" r="6" fill-opacity=".8"/><circle cx="21" cy="30" r="3" fill-opacity=".8"/><circle cx="83" cy="30" r="3" fill-opacity=".8"/></g>`,
  },
  robocop: {
    animate: (hash) =>
      `<rect x="7" y="16" width="91" height="16" rx="4" fill="#000" fill-opacity=".8"/><rect x="7" y="16" width="91" height="16" rx="4" fill="url(#visorShimmer_${hash})"/>`,
    kind: "shimmer",
    needsShimmerDefs: true,
    original: `<rect x="7" y="16" width="91" height="16" rx="4" fill="#000" fill-opacity=".8"/>`,
  },
  round: {
    animate: (hash) => {
      const left = buildEyeGroup(
        { content: `<circle cx="24" cy="30" r="6"/>`, cx: 24, cy: 30 },
        `bRdL_${hash}`,
        1.5,
        {
          begin: 17 + (parseInt(hash, 36) % 12),
          id: `wRdL_${hash}`,
          interval: WINK_LEFT_INTERVAL,
        },
      );
      const right = buildEyeGroup(
        { content: `<circle cx="80" cy="30" r="6"/>`, cx: 80, cy: 30 },
        `bRdR_${hash}`,
        1.5,
        {
          begin: 32 + (parseInt(hash, 36) % 14),
          id: `wRdR_${hash}`,
          interval: WINK_RIGHT_INTERVAL,
        },
      );
      return `<g fill="#fff">${left}${right}</g>`;
    },
    kind: "blink",
    original: `<g fill="#fff"><circle cx="24" cy="30" r="6"/><circle cx="80" cy="30" r="6"/></g>`,
  },
  roundFrame01: {
    animate: (hash) => {
      const left = buildEyeGroup(
        {
          content: `<rect x="24" y="22" width="10" height="12" rx="2" fill="#F4F4F4"/>`,
          cx: 29,
          cy: 28,
        },
        `bRfL_${hash}`,
        2,
        {
          begin: 23 + (parseInt(hash, 36) % 12),
          id: `wRfL_${hash}`,
          interval: WINK_LEFT_INTERVAL,
        },
      );
      const right = buildEyeGroup(
        {
          content: `<rect x="70" y="22" width="10" height="12" rx="2" fill="#F4F4F4"/>`,
          cx: 75,
          cy: 28,
        },
        `bRfR_${hash}`,
        2,
        {
          begin: 41 + (parseInt(hash, 36) % 16),
          id: `wRfR_${hash}`,
          interval: WINK_RIGHT_INTERVAL,
        },
      );
      return `<rect y="12" width="104" height="32" rx="16" fill="#000" fill-opacity=".8"/>${left}${right}`;
    },
    kind: "blink",
    original: `<rect y="12" width="104" height="32" rx="16" fill="#000" fill-opacity=".8"/><rect x="24" y="22" width="10" height="12" rx="2" fill="#F4F4F4"/><rect x="70" y="22" width="10" height="12" rx="2" fill="#F4F4F4"/>`,
  },
  sensor: {
    animate: (hash) => {
      // Single eye → no wink possible
      const eye = buildEyeGroup(
        {
          content: `<circle cx="28" cy="24" r="16" fill="#000" fill-opacity=".6"/><circle cx="34" cy="16" r="3" fill="#fff"/>`,
          cx: 28,
          cy: 24,
        },
        `bSn_${hash}`,
        2.5,
        undefined,
      );
      return `<path d="M28 44a20 20 0 0 0 19.9-18h41.52a5 5 0 1 0 0-4H47.9A20 20 0 1 0 28 44Z" fill="#000" fill-opacity=".2"/><circle cx="94" cy="24" r="2" fill="#fff"/>${eye}`;
    },
    kind: "blink",
    original: `<path d="M28 44a20 20 0 0 0 19.9-18h41.52a5 5 0 1 0 0-4H47.9A20 20 0 1 0 28 44Z" fill="#000" fill-opacity=".2"/><circle cx="94" cy="24" r="2" fill="#fff"/><circle cx="28" cy="24" r="16" fill="#000" fill-opacity=".6"/><circle cx="34" cy="16" r="3" fill="#fff"/>`,
  },
  shade01: {
    animate: (hash) =>
      `<path d="M87 14H17c-3.5 0-5 3-5 5v2c0 2 1.5 5 5 5h12c6 0 11.62 8 17 8h14c5.38 0 9-8 15-8h12c3.5 0 5-3 5-5v-2c0-2-1.5-5-5-5Z" fill="#FF3D3D"/><path d="M87 14H17c-3.5 0-5 3-5 5v2c0 2 1.5 5 5 5h12c6 0 11.62 8 17 8h14c5.38 0 9-8 15-8h12c3.5 0 5-3 5-5v-2c0-2-1.5-5-5-5Z" fill="url(#visorShimmer_${hash})"/>`,
    kind: "shimmer",
    needsShimmerDefs: true,
    original: `<path d="M87 14H17c-3.5 0-5 3-5 5v2c0 2 1.5 5 5 5h12c6 0 11.62 8 17 8h14c5.38 0 9-8 15-8h12c3.5 0 5-3 5-5v-2c0-2-1.5-5-5-5Z" fill="#FF3D3D"/>`,
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Mouth recipes
// ────────────────────────────────────────────────────────────────────────────

interface BarSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  hMin: number;
  hMax: number;
  dur: number;
  begin: number;
  opDur: number;
  opBegin: number;
}

function bouncingBar(b: BarSpec): string {
  const yTop = b.y + (b.h - b.hMax) / 2;
  const yBot = b.y + (b.h - b.hMin) / 2;
  const heights = `${b.h};${b.hMax};${b.h};${b.hMin};${b.h}`;
  const ys = `${b.y};${yTop};${b.y};${yBot};${b.y}`;
  return `<rect x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" rx="2"><animate attributeName="height" values="${heights}" dur="${b.dur}s" begin="${b.begin}s" repeatCount="indefinite"/><animate attributeName="y" values="${ys}" dur="${b.dur}s" begin="${b.begin}s" repeatCount="indefinite"/><animate attributeName="fill-opacity" values="0.45;0.78;0.45" dur="${b.opDur}s" begin="${b.opBegin}s" repeatCount="indefinite"/></rect>`;
}

interface SmileSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  rx: number;
  dip: number;
  dur: number;
  fill: string;
  fillOpacity: number;
}

function smilingMouth(s: SmileSpec): string {
  const x0 = s.x + s.rx;
  const x1 = s.x + s.w - s.rx;
  const xC = s.x + s.w / 2;
  const yT = s.y;
  const yB = s.y + s.h;
  const yM = s.y + s.h / 2;
  const xR = s.x + s.w;
  const flat = `M${x0} ${yT} Q${xC} ${yT} ${x1} ${yT} Q${xR} ${yT} ${xR} ${yM} Q${xR} ${yB} ${x1} ${yB} Q${xC} ${yB} ${x0} ${yB} Q${s.x} ${yB} ${s.x} ${yM} Q${s.x} ${yT} ${x0} ${yT} Z`;
  const smile = `M${x0} ${yT} Q${xC} ${yT + s.dip} ${x1} ${yT} Q${xR} ${yT} ${xR} ${yM} Q${xR} ${yB} ${x1} ${yB} Q${xC} ${yB + s.dip} ${x0} ${yB} Q${s.x} ${yB} ${s.x} ${yM} Q${s.x} ${yT} ${x0} ${yT} Z`;
  return `<path fill="${s.fill}" fill-opacity="${s.fillOpacity}" d="${flat}"><animate attributeName="d" values="${flat};${flat};${smile};${flat};${flat}" keyTimes="0;0.3;0.5;0.7;1" dur="${s.dur}s" repeatCount="indefinite"/></path>`;
}

const DIAGRAM_PATH = `<path d="M9 17h11l2-4 3 7 4-8 2 9 3-11 3 10 3-3h15l3-4 2 7 3-3h4" stroke="#4EFAC9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

interface MouthRecipe {
  original: string;
  animate: (hash: string) => string;
  defs?: (hash: string) => string;
}

const MOUTH_RECIPES: Record<string, MouthRecipe> = {
  diagram: {
    animate: (hash) =>
      `<g clip-path="url(#diagramClip_${hash})"><g>${DIAGRAM_PATH}<g transform="translate(-58 0)">${DIAGRAM_PATH}</g><animateTransform attributeName="transform" type="translate" values="0 0; 58 0" dur="4s" repeatCount="indefinite"/></g></g>`,
    defs: (hash) =>
      `<defs><clipPath id="diagramClip_${hash}"><rect x="8" y="8" width="60" height="16" rx="2"/></clipPath></defs>`,
    original: DIAGRAM_PATH,
  },
  grill01: {
    animate: () =>
      `<g fill="#000">${bouncingBar({ begin: 0, dur: 2.6, h: 8, hMax: 9, hMin: 7, opBegin: -0.3, opDur: 2.1, w: 4, x: 12, y: 12 })}${bouncingBar({ begin: -0.6, dur: 3.1, h: 8, hMax: 9, hMin: 7, opBegin: -1, opDur: 2.4, w: 4, x: 24, y: 12 })}${bouncingBar({ begin: -1.2, dur: 2.8, h: 8, hMax: 9, hMin: 7, opBegin: -0.5, opDur: 1.9, w: 4, x: 36, y: 12 })}${bouncingBar({ begin: -1.8, dur: 3.3, h: 8, hMax: 9, hMin: 7, opBegin: -1.4, opDur: 2.6, w: 4, x: 48, y: 12 })}${bouncingBar({ begin: -2.4, dur: 2.5, h: 8, hMax: 9, hMin: 7, opBegin: -0.8, opDur: 2, w: 4, x: 60, y: 12 })}</g>`,
    original: `<g fill="#000" fill-opacity=".6"><rect x="12" y="12" width="4" height="8" rx="2"/><rect x="36" y="12" width="4" height="8" rx="2"/><rect x="24" y="12" width="4" height="8" rx="2"/><rect x="48" y="12" width="4" height="8" rx="2"/><rect x="60" y="12" width="4" height="8" rx="2"/></g>`,
  },
  grill02: {
    animate: () =>
      `<g fill="#000">${bouncingBar({ begin: 0, dur: 2.9, h: 14, hMax: 15.5, hMin: 12.5, opBegin: -0.4, opDur: 2.2, w: 6, x: 14, y: 10 })}${bouncingBar({ begin: -0.8, dur: 3.4, h: 14, hMax: 15.5, hMin: 12.5, opBegin: -1.2, opDur: 2.7, w: 6, x: 28, y: 10 })}${bouncingBar({ begin: -1.6, dur: 3, h: 14, hMax: 15.5, hMin: 12.5, opBegin: -0.6, opDur: 2, w: 6, x: 42, y: 10 })}${bouncingBar({ begin: -2.4, dur: 3.6, h: 14, hMax: 15.5, hMin: 12.5, opBegin: -1.5, opDur: 2.4, w: 6, x: 56, y: 10 })}</g>`,
    original: `<g fill="#000" fill-opacity=".6"><rect x="28" y="10" width="6" height="14" rx="2"/><rect x="14" y="10" width="6" height="14" rx="2"/><rect x="42" y="10" width="6" height="14" rx="2"/><rect x="56" y="10" width="6" height="14" rx="2"/></g>`,
  },
  grill03: {
    animate: () =>
      `<path fill="#000" d="M18 9h4v14h-4zM42 9h4v14h-4zM30 9h4v14h-4zM54 9h4v14h-4z"><animate attributeName="fill-opacity" values="0.08;0.18;0.08" dur="2.8s" repeatCount="indefinite"/></path>`,
    original: `<path fill="#000" fill-opacity=".1" d="M18 9h4v14h-4zM42 9h4v14h-4zM30 9h4v14h-4zM54 9h4v14h-4z"/>`,
  },
  square01: {
    animate: () =>
      smilingMouth({
        dip: 2.5,
        dur: 5,
        fill: "#000",
        fillOpacity: 0.8,
        h: 8,
        rx: 4,
        w: 27,
        x: 24,
        y: 6,
      }),
    original: `<rect x="24" y="6" width="27" height="8" rx="4" fill="#000" fill-opacity=".8"/>`,
  },
  square02: {
    animate: () =>
      smilingMouth({
        dip: 2,
        dur: 5.5,
        fill: "#000",
        fillOpacity: 0.8,
        h: 4,
        rx: 2,
        w: 44,
        x: 16,
        y: 8,
      }),
    original: `<rect x="16" y="8" width="44" height="4" rx="2" fill="#000" fill-opacity=".8"/>`,
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Public entrypoint
// ────────────────────────────────────────────────────────────────────────────

/**
 * Detect the eye + mouth fragments inside a dicebear bottts-neutral SVG and
 * swap them for animated versions. Per-actor unique IDs are derived from
 * `seed` so multiple inlined SVGs in the same DOM don't collide on SMIL
 * `id` references in begin chains.
 *
 * Pass-through if neither fragment is recognized — the original SVG is
 * returned unchanged.
 */
export function applyAvatarAnimations(svg: string, seed: string): string {
  const hash = hashSeed(seed);
  let result = svg;
  let injectedDefs = "";

  for (const recipe of Object.values(EYE_RECIPES)) {
    if (result.includes(recipe.original)) {
      result = result.replace(recipe.original, recipe.animate(hash));
      if (recipe.needsShimmerDefs) {
        injectedDefs += shimmerDefs(hash);
      }
      break;
    }
  }

  for (const recipe of Object.values(MOUTH_RECIPES)) {
    if (result.includes(recipe.original)) {
      result = result.replace(recipe.original, recipe.animate(hash));
      if (recipe.defs) {
        injectedDefs += recipe.defs(hash);
      }
      break;
    }
  }

  if (injectedDefs) {
    result = result.replace(/(<svg [^>]*>)/, `$1${injectedDefs}`);
  }

  return result;
}
